import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { unlinkEddaAccount } from "../../../services/userServices";
import { formatDate } from "../../../helpers/utils";

import styles from "./index.module.scss";

interface IModalContent {
  bankName?: string;
  accountNumber?: string;
  linkedAt?: string;
  status?: string;
  transactionId?: string;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function ModalContent({
  bankName,
  accountNumber,
  linkedAt,
  status,
  transactionId,
  isLoading,
  onConfirm,
  onCancel,
}: IModalContent) {
  return (
    <div className={styles.modalContainer}>
      <h2 className={styles.modalTitle}>Unlink eDDA Account</h2>
      <p className={styles.modalWarning}>
        Are you sure you want to unlink this account?
      </p>
      <AccountDetailsSection
        bankName={bankName}
        accountNumber={accountNumber}
        linkedAt={linkedAt}
        status={status}
      />
      <div className={styles.modalFooter}>
        <Button
          className={styles.cancelButton}
          variant="outline-secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          className={styles.submitButton}
          variant="danger"
          onClick={onConfirm}
          disabled={!transactionId || isLoading}
        >
          {isLoading ? "Unlinking..." : "Unlink"}
        </Button>
      </div>
    </div>
  );
}

interface IAccountDetailsSection {
  bankName?: string;
  accountNumber?: string;
  linkedAt?: string;
  status?: string;
}

function AccountDetailsSection({
  bankName,
  accountNumber,
  linkedAt,
  status,
}: IAccountDetailsSection) {
  const formatStatus = (statusString?: string) => {
    if (!statusString) return "";
    return statusString
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className={styles.accountDetailsSection}>
      <p className={styles.sectionLabel}>Account Details</p>
      <div className={styles.detailsGrid}>
        {bankName && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Bank Name:</span>
            <span className={styles.detailValue}>{bankName}</span>
          </div>
        )}
        {accountNumber && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Account Number:</span>
            <span className={styles.detailValue}>{accountNumber}</span>
          </div>
        )}
        {linkedAt && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Linked on:</span>
            <span className={styles.detailValue}>
              {formatDate(linkedAt, "d MMM yyyy", "Asia/Singapore")}
            </span>
          </div>
        )}
        {status && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Status:</span>
            <span
              className={`${styles.detailValue} ${
                styles[`status-${status?.toLowerCase()}`]
              }`}
            >
              {formatStatus(status)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

interface IUnlinkEddaAccountModal {
  transactionId?: string;
  bankName?: string;
  accountNumber?: string;
  linkedAt?: string;
  status?: string;
  isVisible: boolean;
  onClose: () => void;
  onRefetch: () => void;
}

export default function UnlinkEddaAccountModal({
  transactionId,
  bankName,
  accountNumber,
  linkedAt,
  status,
  onClose,
  onRefetch,
  isVisible = false,
}: IUnlinkEddaAccountModal) {
  const { userId } = useParams();

  const mutation = useMutation({
    mutationFn: (id: string) => unlinkEddaAccount(userId || "", id),
    onSuccess: () => {
      toast.success("eDDA account unlinked successfully");
      onRefetch();
      onClose();
    },
    onError: (error: Error) => {
      toast.error(
        `An error happened. Please reach out to the engineering team with the following data: error: ${error.message}, userId: ${userId}, transactionId: ${transactionId}`
      );
    },
  });

  const onConfirmUnlink = () => {
    if (!transactionId) return;
    mutation.mutate(transactionId);
  };

  return (
    <Modal
      id="unlinkEddaAccountModal"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      show={isVisible}
      onHide={onClose}
    >
      <Modal.Body>
        <ModalContent
          bankName={bankName}
          accountNumber={accountNumber}
          linkedAt={linkedAt}
          status={status}
          transactionId={transactionId}
          isLoading={mutation.isLoading}
          onConfirm={onConfirmUnlink}
          onCancel={onClose}
        />
      </Modal.Body>
    </Modal>
  );
}
